<template>
  <div
    :class="[loaded ? 'bg-truegray-100' : 'bg-truegray-500']"
    class="min-h-screen w-full"
  >
    <!-- Content -->
    <div
      class="lg:h-72 flex justify-start items-end p-12 bg-cover bg-norepeat relative text-white"
      :style="[
        { backgroundImage: 'url(/img/cw-marketing_hero.jpg)' },
        { backgroundPosition: '0% 70%' },
      ]"
    >
      <div
        class="h-32 border-l-4 pl-6 border-white flex flex-col justify-end items-end m-0 p-0 w-full"
      >
        <h1 class="w-full text-left">Hootmail</h1>
        <span class="text-lg w-full text-left"
          >Stay up to date with the latest updates from the CareerWise
          team.</span
        >
      </div>
      <img
        class="w-40 absolute right-8 bottom-0 mb-6"
        src="/img/cw-logo-white.png"
        alt="CareerWise Logo"
      />
    </div>

    <div class="w-full px-8 py-16 lg:p-16">
      <div
        v-if="!loaded"
        class="h-72 flex flex-col justify-center items-center"
      >
        <lottie :options="loadingAnimationOptions" />
        <span class="text-white text-center">Loading Hootmail</span>
      </div>

      <div
        v-if="loaded"
        class="w-full grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-6"
      >
        <div
          v-for="(item, i) in hootmail"
          :key="i"
          class="w-full rounded-xl relative shadow px-6 py-4"
          :class="
            item.updates_complete
              ? 'bg-truegray-50 hover:bg-white opacity-30 hover:opacity-100 border border-truegray-300 transition-all duration-300'
              : 'bg-white'
          "
        >
          <div class="w-full">
            <div class="w-full flex">
              <div class="w-1/2">
                <h5 class="text-xs text-cw-alt font-bold">{{ item.id }}</h5>
                <h2 class="text-base m-0">{{ item.title }}</h2>
              </div>

              <div class="w-1/2 flex flex-wrap justify-end">
                <button
                  v-if="item.migration_required && !item.updates_complete"
                  :disabled="thinkingUpdates"
                  type="submit"
                  :class="thinkingUpdates ? 'bg-cw-alt' : ''"
                  class="btn btn-cw-alt btn-gradient relative m-0 px-4 py-2"
                  @click="runUpdates(item.id)"
                >
                  <div
                    class="absolute inset-0 w-full flex justify-center items-center z-20"
                  >
                    <lottie
                      v-if="thinkingUpdates"
                      :options="loadingAnimationOptions"
                    />
                  </div>
                  <span :class="[thinkingUpdates ? 'invisible' : '']"
                    >Update</span
                  >
                </button>

                <button
                  v-if="!item.updates_complete && !item.migration_required"
                  :disabled="thinkingUpdates"
                  type="submit"
                  :class="thinkingUpdates ? 'bg-cw-alt' : ''"
                  class="btn btn-cw-alt btn-gradient relative m-0 px-4 py-2"
                  @click="acknowledgeItem(item)"
                >
                  <div
                    class="absolute inset-0 w-full flex justify-center items-center z-20"
                  >
                    <lottie
                      v-if="thinkingUpdates"
                      :options="loadingAnimationOptions"
                    />
                  </div>
                  <span :class="[thinkingUpdates ? 'invisible' : '']"
                    >Mark as read</span
                  >
                </button>

                <p
                  v-if="item.updates_complete"
                  class="flex text-black font-bold space-x-4"
                >
                  Updated
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    class="w-5 h-5 ml-4"
                  >
                    <path
                      fill-rule="evenodd"
                      d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z"
                      clip-rule="evenodd"
                    />
                  </svg>
                </p>
                <!-- <h6  v-if="item.updates_complete && item.migration_required" class="text-right w-2/3 italic pr-2 mt-2">An application refresh may be required for this update.</h6> -->
              </div>
            </div>

            <div class="w-full my-4">
              <h6 :style="{ fontSize: '12px' }" class="text-truegray-500">
                {{ item.description }}
              </h6>
            </div>

            <div class="w-full flex items-center justify-between">
              <h6 class="text-cw-alt cursor-pointer" @click="showModal(item)">
                Read more
              </h6>
              <h6 class="text-truegray-500 font-bold">
                Published: {{ item.created_at }}
              </h6>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Readmore Modal -->
    <div
      v-if="show_update_modal"
      id="show_update_modal"
      class="fixed z-50 inset-0 overflow-y-auto"
      aria-labelledby="modal-title"
      role="dialog"
      aria-modal="true"
    >
      <div
        class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
        ></div>
        <!-- This element is to trick the browser into centering the modal contents. -->
        <span
          class="hidden sm:inline-block sm:align-middle sm:h-screen"
          aria-hidden="true"
          >&#8203;</span
        >
        <!-- Modal panel, show/hide based on modal state. -->
        <div
          class="inline-block align-center bg-white rounded-lg p-6 text-left overflow-visible shadow transform transition-all mt-8 mb-20 align-middle max-w-3xl w-full lg:p-16 relative"
        >
          <div v-if="active_update" class="flex flex-col">
            <h5
              v-if="active_update.id"
              class="text-xs text-cw-alt font-bold mb-4"
            >
              Hootmail Update #{{ active_update.id }}
            </h5>
            <h2 v-if="active_update.title" class="text-base m-0 mb-1">
              {{ active_update.title }}
            </h2>
            <h6
              v-if="active_update.description"
              :style="{ fontSize: '14px' }"
              class="text-truegray-500 mb-4"
            >
              {{ active_update.description }}
            </h6>
            <p
              v-if="active_update.body"
              class="text-truegray-500 update-body"
              v-html="active_update.body"
            ></p>

            <button
              type="button"
              class="absolute right-8 top-6 transform hover:rotate-180 transition-all duration-300 hover:text-primary"
              @click="show_update_modal = !show_update_modal"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { shared } from '../../../../js/mixins/shared.js'
import { mapGetters } from 'vuex'
import { EventBus } from '../../../../js/plugins/event-bus.js'
import * as loadingAnimation from '../../../../shared/components/loading.json'

export default {
  mixins: [shared],

  data() {
    return {
      loaded: false,
      loadingAnimationOptions: { animationData: loadingAnimation.default },
      loadingAnimate: false,
      hootmail: [], // MODEL = Alerts
      thinkingUpdates: false,
      active_update: null,
      show_update_modal: false,
    }
  },

  computed: {
    ...mapGetters({
      tenantId: 'shared/getTenantId',
    }),
  },

  mounted() {
    this.getUpdates()
  },

  methods: {
    getUpdates() {
      // MODEL = Alerts
      axios.get('/api/auth/updates/' + this.tenantId).then((response) => {
        let result = response.data
        if (result.success) {
          this.$set(this, 'hootmail', result.updates)
          this.$set(this, 'loaded', true)

          let updates_required = 0
          result.updates.forEach((e) => {
            if (e.migration_required) {
              updates_required = updates_required + 1
            }
          })
          if (!updates_required) {
            this.$set(this, 'updates_complete', true)
          }
        }
      })
    },

    runUpdates(alert_id) {
      this.$set(this, 'thinkingUpdates', true)
      let submitData = {}
      submitData.alert_id = alert_id
      submitData.tenant_id = this.tenantId

      axios.post('/api/auth/run-updates', submitData).then((response) => {
        let result = response.data
        if (result.success) {
          this.$set(this, 'thinkingUpdates', false)
          // EventBus.$emit('updateViewed')
          // EventBus.$emit('reloadWebsiteSettings')
          this.getUpdates()
          window.location.reload()
        }
      })
    },

    showModal(item) {
      this.$set(this, 'active_update', item)
      this.$set(this, 'show_update_modal', true)
    },

    acknowledgeItem(item) {
      this.$set(this, 'thinkingUpdates', true)
      item.updates_complete = true
      let submitData = {}
      submitData.alert_id = item.id
      submitData.tenant_id = this.tenantId

      axios
        .post('/api/auth/mark-update-complete', submitData)
        .then((response) => {
          let result = response.data
          if (result.success) {
            this.$set(this, 'thinkingUpdates', false)
            EventBus.$emit('updateViewed')
          }
        })
    },
  },
}
</script>

<style>
.update-body ul,
.update-body li {
  list-style: disc;
}

.update-body ul {
  margin: 10px 0 10px 10px;
}

.update-body ul ul,
.update-body ul ul li {
  list-style: circle;
}
</style>

<style scoped>
.btn-gradient {
  background: linear-gradient(225deg, #ff4778 0%, rgba(255, 71, 120, 0) 71.34%),
    #6f36bc !important;
  color: #fff !important;
  border: none;
}

.hootmail-top {
  background: linear-gradient(
    90deg,
    rgba(255, 71, 120, 1) 11%,
    rgba(169, 134, 215, 1) 100%
  );
}

.hootmail-body * {
  font-size: 0.875rem;
}

.show-text {
  height: auto !important;
}

.post-intro::after {
  content: '';
  background: #fff;
  height: 1px;
  width: 500px;
  min-width: 95%;
  position: absolute;
  top: 4rem;
  right: 0;
}

@media screen and (max-width: 1024px) {
  .post-intro::after {
    display: none;
  }
}
</style>
